import { BreakpointObserver } from '@angular/cdk/layout';
import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import * as dayjs from 'dayjs';
import { Subject, takeUntil } from 'rxjs';
import { mxvAnimations } from 'src/@tolis/animations';

@Component({
  selector: 'app-inline-calendar',
  templateUrl: './inline-calendar.component.html',
  styleUrls: ['./inline-calendar.component.css'],
  encapsulation: ViewEncapsulation.None,
  animations: mxvAnimations,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class InlineCalendarComponent
  implements OnInit, OnChanges, AfterViewChecked, OnDestroy
{
  private _unsubscribeAll: Subject<any> = new Subject<any>();

  @Input() bookedDays;
  @Input() startDate;
  @Input() endDate;
  @Input() calendarLayout = 'inline';
  @Input() compact = false;
  @Input() minstays = [];
  smallDevice = false;
  @Input() initElement = '';
  @Input() calendarOptions: CalendarOptions = new CalendarOptions();

  @Output() datesSelected = new EventEmitter<{ from: string; to: string }>();
  @Output() calendarIsVisible = new EventEmitter<{ visible: boolean }>();
  isVisible = false;

  daysToDisable = [];
  public days: any[];
  public nextMonthDays: any[];
  public selectionLabel = 'Select Your Dates';
  public dayIsHovered = null;
  selectedStartDate = null;
  selectedEndDate = null;
  fakeEndDate = null;

  startMonth = dayjs().startOf('month');
  nextMonth = dayjs().startOf('month').add(1, 'month');

  constructor(
    private changeDetector: ChangeDetectorRef,
    private _observer: BreakpointObserver,
  ) {
    // this.startMonth = moment().startOf('month');
    // this.nextMonth = moment().startOf('month').add(1, 'months');

    this.daysToDisable = [];
    this.bookedDays = [];
  }

  buildCalendar(): void {
    const requestedStart = dayjs(this.startDate);
    this.startMonth = (
      requestedStart.isValid() ? requestedStart : dayjs()
    ).startOf('month');

    this.nextMonth = this.startMonth.add(1, 'month');

    const startMonthDayCount = this.startMonth.daysInMonth();
    const nextMonthDayCount = this.nextMonth.daysInMonth();

    this.days = [...Array(startMonthDayCount)].map((_, i) =>
      this.startMonth.clone().add(i, 'day'),
    );
    this.nextMonthDays = [...Array(nextMonthDayCount)].map((_, i) =>
      this.nextMonth.clone().add(i, 'day'),
    );
    this.selectedStartDate = null;
    this.selectedEndDate = null;

    if (this.startDate && this.endDate) {
      this.selectedStartDate = null;
      this.selectedEndDate = null;

      const dtStart = dayjs(this.startDate);
      const dtEnd = dayjs(this.endDate);
      if (this.checkMinStayRestrictions(this.startDate, this.endDate)) {
        this.onCalendarClicked(dtStart, false);
        this.onCalendarClicked(dtEnd, false);
      }
    }
  }
  ngOnInit() {
    this.buildCalendar();
    this._observer
      .observe('(max-width: 784px)')
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((result) => {
        this.smallDevice = result.matches;
      });
  }
  ngOnDestroy(): void {
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  ngAfterViewChecked(): void {
    this.changeDetector.markForCheck();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.startDate) {
      this.startDate = changes.startDate.currentValue;
      this.buildCalendar();
      this.changeDetector.markForCheck();
    }
    if (changes.bookedDays) {
      if (changes.bookedDays.currentValue) {
        changes.bookedDays.currentValue.forEach((d) => {
          const dates = this.getDates(
            new Date(d.from_date),
            new Date(d.to_date),
          );
          dates.shift();
          this.daysToDisable = Array.from(
            new Set(this.daysToDisable.concat(dates)),
          );
        });

        // start and end dates
        // this.close();
        // this.open();
      }
    }
  }
  private swipeCoord?: [number, number];
  private swipeTime?: number;
  swipe(e: TouchEvent, when: string): void {
    const coord: [number, number] = [
      e.changedTouches[0].clientX,
      e.changedTouches[0].clientY,
    ];
    const time = new Date().getTime();
    if (when === 'start') {
      this.swipeCoord = coord;
      this.swipeTime = time;
    } else if (when === 'end') {
      const direction = [
        coord[0] - this.swipeCoord[0],
        coord[1] - this.swipeCoord[1],
      ];
      const duration = time - this.swipeTime;
      if (
        duration < 1000 && //
        Math.abs(direction[0]) > 30 && // Long enough
        Math.abs(direction[0]) > Math.abs(direction[1] * 3)
      ) {
        // Horizontal enough
        const swipe = direction[0] < 0 ? 'next' : 'previous';
        if (direction[0] < 0) {
          this.next();
        } else {
          this.prev();
        }
      }
    }
  }

  getDates(startDate, endDate) {
    const dayInterval = 1000 * 60 * 60 * 24; // 1 day
    const duration = endDate - startDate;
    const steps = duration / dayInterval;
    return Array.from(
      { length: steps },
      (v, i) =>
        new Date(startDate.valueOf() + dayInterval * i)
          .toISOString()
          .split('T')[0],
    );
  }

  column(index, m) {
    if (index === 0) {
      return m[0].day() + 1;
    }
  }

  today(day) {
    return dayjs().isSame(day, 'day');
  }

  checkMinStayRestrictions(startDate, endDate) {
    if (this.minstays?.length <= 0) {
      return true;
    }

    const start = dayjs(startDate);
    const end = dayjs(endDate);

    let currentLoopDate = dayjs(startDate);

    let minStayChecksOk = true;

    while (currentLoopDate < end) {
      let currentMinStay =
        this.minstays[currentLoopDate.format('YYYY-MM-DD')] ?? 7;

      if (currentMinStay > end.diff(start, 'days')) {
        minStayChecksOk = false;
        break;
      }

      currentLoopDate = currentLoopDate.add(1, 'days');
    }

    return minStayChecksOk;
  }

  isOccupied(day, doMoment?: boolean) {
    const isoDate = doMoment
      ? day.format('YYYY-MM-DD')
      : day.toISOString().split('T')[0];

    const today = dayjs();

    if (day.diff(today, 'days') < 0) {
      return true;
    }

    return this.daysToDisable.includes(isoDate);
  }

  counter: number = 0;
  next() {
    this.startMonth = this.startMonth.add(1, 'month');

    this.nextMonth = this.nextMonth.add(1, 'month');

    this.days = [...Array(this.startMonth.daysInMonth())].map((_, i) =>
      this.startMonth.clone().add(i, 'day'),
    );
    this.nextMonthDays = [...Array(this.nextMonth.daysInMonth())].map((_, i) =>
      this.nextMonth.clone().add(i, 'day'),
    );

    this.counter++;
  }

  prev() {
    this.startMonth = this.startMonth.add(-1, 'month');
    this.nextMonth = this.nextMonth.add(-1, 'month');

    this.days = [...Array(this.startMonth.daysInMonth())].map((_, i) =>
      this.startMonth.clone().add(i, 'day'),
    );
    this.nextMonthDays = [...Array(this.nextMonth.daysInMonth())].map((_, i) =>
      this.nextMonth.clone().add(i, 'day'),
    );
    this.counter--;
  }

  onCalendarClicked(day, close: boolean = true): boolean {
    // if this is a single select calendar just return the date clicked
    if (!this.calendarOptions.multiple) {
      this.changeDetector.markForCheck();
      this.datesSelected.emit(day);

      return true;
    }

    // if day clicked not available return
    if (this.isOccupied(day, true)) {
      this.selectionLabel = 'Dates are not Available';
      return false;
    }

    // new selection
    if (this.selectedStartDate !== null && this.selectedEndDate !== null) {
      this.selectedEndDate = null;
      this.selectedStartDate = day;
      this.selectionLabel = day.format('DD MMM, YYYY');

      return true;
    }

    if (this.selectedStartDate === null && this.selectedEndDate === null) {
      this.selectedStartDate = day;
      this.selectionLabel = day.format('DD MMM, YYYY');

      return true;
    }

    if (this.selectedStartDate !== null && this.selectedEndDate === null) {
      // if enddate is before startdate swap them

      if (day.diff(this.selectedStartDate) < 0) {
        const newDay = day;
        day = this.selectedStartDate;
        this.selectedStartDate = newDay;
      }

      const startDate = new Date(this.selectedStartDate.format('YYYY-MM-DD'));
      const endDate = new Date(day.format('YYYY-MM-DD'));
      const totalNights =
        day.diff(this.selectedStartDate) / (1000 * 60 * 60 * 24);
      // check if there are unavailable dates between start and end first
      const dates = this.getDates(startDate, endDate);
      const intersection = dates.filter((x) => this.daysToDisable.includes(x));

      this.selectedEndDate = intersection.length > 1 ? null : day;
      // reset start date if this is false
      this.selectedStartDate =
        intersection.length > 1 ? null : this.selectedStartDate;
      this.selectionLabel =
        intersection.length > 1
          ? 'Dates Are not Available'
          : this.selectedStartDate.format('DD MMM, YYYY ') +
            ' - ' +
            day.format('DD MMM, YYYY ') +
            ' | ' +
            Math.round(totalNights) +
            ' Nights';

      // emit if valid
      if (intersection.length < 1) {
        if (close) {
          // before final selection check minimum stay
          if (
            this.checkMinStayRestrictions(
              this.selectedStartDate.format('YYYY-MM-DD'),
              this.selectedEndDate.format('YYYY-MM-DD'),
            )
          ) {
            // this will emmit and close
            this.changeDetector.markForCheck();
            this.datesSelected.emit({
              from: this.selectedStartDate.format('YYYY-MM-DD'),
              to: this.selectedEndDate.format('YYYY-MM-DD'),
            });
            return intersection.length < 1;
          }

          this.selectionLabel =
            'The Minimum Stay is Longer than the dates Selected';

          this.selectedEndDate = null;
          this.selectedStartDate = null;
          return false;
        }
      }

      return intersection.length < 1;
    }

    this.selectedStartDate = null;
    this.selectedEndDate = null;
    this.selectedStartDate = day;
    return true;
  }

  close() {
    this.changeDetector.markForCheck();
    this.calendarIsVisible.emit({ visible: false });
  }
  open() {
    this.changeDetector.markForCheck();
    this.calendarIsVisible.emit({ visible: true });
  }

  // @HostListener('document:click', ['$event'])
  // clickout(event) {
  //   if (
  //     !this.eRef.nativeElement.contains(event.target) &&
  //     event.target.name !== 'calendarTrigger'
  //   ) {
  //     this.close();
  //   }
  // }

  setFakeEndDate(nmday) {
    this.fakeEndDate = nmday;
  }

  isInDisplayedRange(day: any): boolean {
    if (!this.selectedStartDate) {
      return false;
    }

    const rangeEnd = this.selectedEndDate || this.fakeEndDate;
    if (!rangeEnd) {
      return false;
    }

    const startValue = this.selectedStartDate.valueOf();
    const endValue = rangeEnd.valueOf();
    const minValue = Math.min(startValue, endValue);
    const maxValue = Math.max(startValue, endValue);
    const dayValue = day.valueOf();

    return dayValue >= minValue && dayValue <= maxValue;
  }
}

export interface ICalendarOptions {
  showTitle: boolean;
  multiple: boolean;
  minstays?: Minstay[];
}
export interface MinstayKey {
  [key: string]: string;
}
export interface Minstay {
  key: MinstayKey;
  minstay: number;
}

export class CalendarOptions implements ICalendarOptions {
  public showTitle: boolean = true;
  public multiple: boolean = true;
  public minstays?: Minstay[] = [];
}
